import getOr from 'lodash/get';
import isEmpty from 'lodash/fp/isEmpty';
import { formatDate, toExpenseArray, toNumber } from 'utils/utils';

import {
  Assistance,
  assistanceList,
  EmploymentData,
  FormDataType,
  IncomeData,
  DependentData,
} from 'contexts/FormProvider';

type YesNoString = 'Yes' | 'yes' | 'No' | 'no';
type TrueFalseString = 'True' | 'False';

const toBooleanString = (value: YesNoString): TrueFalseString => {
  const lowercaseValue = value.toLowerCase();

  if (lowercaseValue === 'yes') {
    return 'True';
  }
  return 'False';
};

const assistanceListValues = [
  'ExtensionOfDueDateForPayment',
  'PayingAReducedLumpSum',
  'PayingInInstalments',
  'PostponingOneOrMoreInstalments',
  'Other',
];

const isDeclared = 'TRUE';

export const remapFHA = (data: FormDataType) => {
  const get = (key: keyof FormDataType, defaultValue: string | null = null) => {
    if (isEmpty(data)) {
      return null;
    }

    return getOr(data, key, defaultValue);
  };

  const getAssistanceIndex = assistanceList.findIndex((a: Assistance) => get('assistance') === a);

  const dependents = [...getOr(data, 'dependents', [])].map(({ dependentName, dependentAge }: DependentData) => ({
    dependent_name: dependentName,
    dependent_age: dependentAge,
  }));

  const isSameAddress = String(get('addressType'))[0].toUpperCase() + String(get('addressType')).slice(1);

  const isEmployed = get('employmentStatus') === 'yes';

  const employmentDataArray = [...getOr(data, 'employmentData', [])].map((cur: EmploymentData) => ({
    employment_type: cur.employmentType,
    employer_name: cur.employerName,
    occupation: cur.occupation,
    contact_name: cur.contactPerson,
    contact_number: cur.contactPersonNumber,
    salary_per_month: toNumber(cur.salary),
  }));

  const employmentData = isEmployed ? employmentDataArray : null;

  const haveIncome = get('incomeStatus') === 'yes';

  const incomeDataArray = [...getOr(data, 'incomeData', [])].map((cur: IncomeData) => ({
    income_type: cur.incomeType,
    income_description: cur.incomeDescription,
    income_amount_per_month: toNumber(cur.incomeAmount),
  }));

  const incomes = haveIncome ? incomeDataArray : null;

  const expenses = {
    rent_mortgage: get('rentMortgage'),
    child_support: get('childSupport'),
    other_loan_payments: get('otherLoan'),
    motor_vehicle_expenses: get('motorVehicle'),
    credit_card: get('creditCard'),
    living_costs: get('livingCosts'),
    utilities: get('utilities'),
    school_fees: get('schoolFees'),
    hospital_medical: get('hospitalMedicalCosts'),
    insurance: get('insurance'),
    other: get('otherExpense'),
  };

  return {
    your_details: {
      contact_reason: {
        policy_or_claim: 'claim',
        claim_number: get('claimNumber'),
      },

      personal_details: {
        relationship: get('relationship'),
        first_name: get('firstName'),
        last_name: get('lastName'),
        organisation: get('organisation'),
      },

      contact_details: {
        email_address: get('emailAddress'),
        phone_number: get('phoneNumber'),
      },
    },

    insured_details: {
      insured_details: {
        title: get('title'),
        first_name: get('insuredFirstName'),
        last_name: get('insuredLastName'),
        dependent: isEmpty(dependents) ? null : dependents,
      },

      contact_details: {
        language_interpreter_required: toBooleanString(get('isLanguageInterpreter') as YesNoString),
        language: get('isLanguageInterpreter') === 'yes' ? get('language') : null,
        other_language: get('language') === 'Other' ? get('otherLanguage') : null,
        preferred_method_of_contact: get('iContactPreferedMethod'),
        email_address: get('cEmailAddress'),
        physical_address: {
          country: get('rCountry'),
          street_address: get('rStreetAddress'),
          suburb: get('rSuburb'),
          state: get('rState'),
          postcode: get('rPostcode'),
        },
        postal_address_is_same_as_physical_address: isSameAddress,
        postal_address:
          get('addressType') === 'no'
            ? {
                country: get('pCountry'),
                street_address: get('pStreetAddress'),
                suburb: get('pSuburb'),
                state: get('pState'),
                postcode: get('pPostcode'),
              }
            : null,
        mobile_phone: get('mPhoneNumber'),
        home_phone: get('hPhoneNumber'),
        other_phone: get('oPhoneNumber'),
      },
    },

    tell_us_more: {
      hardship: {
        date_of_application: data.dateOfApplication ? formatDate(data.dateOfApplication) : null,
        source_of_application: get('sourceOfApplication') === 'Online form' ? 'Webform' : get('sourceOfApplication'),
        circumstances: get('reason'),
        nature_of_assistance: assistanceListValues[getAssistanceIndex],
        assistance_description: get('description'),
      },
    },
    employment: {
      current_employment: toBooleanString(get('employmentStatus') as YesNoString),
      employment: employmentData,
    },

    income: {
      other_income: toBooleanString(get('incomeStatus') as YesNoString),
      income: incomes,
      expenses: toExpenseArray(expenses),
      declaration: isDeclared,
    },
  };
};
